import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../firebase';
import { createImageLqipDataUrl, resizeImageToMaxWidth } from './imageResize';

function safeFilename(name) {
  return String(name || 'image')
    .trim()
    .replace(/[^a-z0-9._-]/gi, '_')
    .replace(/_+/g, '_')
    .slice(0, 120);
}

/**
 * Upload an image file to Firebase Storage and return a public download URL.
 *
 * @param {Object} params
 * @param {File} params.file
 * @param {string} params.folder - e.g. "posts/<docId>" or "posts/drafts/<slug>"
 * @param {(pct: number) => void} [params.onProgress]
 * @returns {Promise<{ url: string, path: string }>}
 */
export function uploadImageToStorage({ file, folder, onProgress }) {
  if (!file) throw new Error('No file provided');
  const contentType = String(file.type || '');
  if (!contentType.startsWith('image/')) {
    throw new Error('Only image files are supported.');
  }

  const name = safeFilename(file.name);
  const path = `${String(folder || 'uploads').replace(/\/+$/g, '')}/${Date.now()}-${name}`;
  const storageRef = ref(storage, path);

  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType,
    // Strong caching for our own uploaded images (Firebase Storage supports this metadata).
    // This reduces re-downloads even without a service worker.
    cacheControl: 'public,max-age=31536000,immutable',
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snap) => {
        if (typeof onProgress === 'function' && snap.totalBytes) {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress(pct);
        }
      },
      (err) => reject(err),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({ url, path });
      }
    );
  });
}

/**
 * Upload optimized variants for a post image:
 * - imageUrl: ~1600px wide (good for hero/detail pages)
 * - imageThumbUrl: ~900px wide (fast for cards)
 */
export async function uploadPostImageWithThumbnail({ file, folder, onProgress }) {
  if (!file) throw new Error('No file provided');
  const baseFolder = String(folder || 'uploads').replace(/\/+$/g, '');

  // Resize on the client to dramatically reduce transfer size and decode time.
  const full = await resizeImageToMaxWidth(file, { maxWidth: 1600, type: 'image/webp', quality: 0.82 });
  const thumb = await resizeImageToMaxWidth(file, { maxWidth: 900, type: 'image/webp', quality: 0.78 });
  const lqip = await createImageLqipDataUrl(file, { maxWidth: 32, quality: 0.55 });

  const nameBase = safeFilename(file.name).replace(/\.[a-z0-9]+$/i, '');
  const fullFile = new File([full.blob], `${nameBase || 'image'}-full.${full.ext}`, { type: full.contentType });
  const thumbFile = new File([thumb.blob], `${nameBase || 'image'}-thumb.${thumb.ext}`, { type: thumb.contentType });

  // Split progress roughly between the two uploads.
  let p1 = 0;
  let p2 = 0;
  const push = () => {
    if (typeof onProgress !== 'function') return;
    onProgress(Math.round((p1 * 0.65) + (p2 * 0.35)));
  };

  const uploadedFull = await uploadImageToStorage({
    file: fullFile,
    folder: `${baseFolder}/full`,
    onProgress: (pct) => {
      p1 = pct;
      push();
    },
  });

  const uploadedThumb = await uploadImageToStorage({
    file: thumbFile,
    folder: `${baseFolder}/thumb`,
    onProgress: (pct) => {
      p2 = pct;
      push();
    },
  });

  return { imageUrl: uploadedFull.url, imageThumbUrl: uploadedThumb.url, imageLqip: lqip };
}


